// src/components/chat/SelectionOptions.js
import { useState, useRef, useEffect } from "react";

// Global store to persist selection state across component instances
const selectionStore = new Map();

export default function SelectionOptions({
  options,
  disabled,
  onContinue,
  messageId, // Add messageId to identify unique questions
  allowMultiple = false, // New prop for multiselection mode
  showContinueButton = false, // Show Continue button for multi-select (Android/iOS)
  preselected = [], // Options a named technology already answered - locked, pre-checked
  slot = null // Slot from message to detect confirmation mode
}) {

  const preselectedList = Array.isArray(preselected) ? preselected : [];
  const preselectedKey = JSON.stringify(preselectedList);
  const isLocked = (option) => allowMultiple && preselectedList.includes(option);

  // Use messageId as key for persistence, fallback to options string
  const storeKey = messageId || JSON.stringify(options);

  // Initialize from store or create new
  const storeData = selectionStore.get(storeKey);
  const initialSelected = allowMultiple
    ? Array.from(new Set([...(storeData?.selected || []), ...preselectedList]))
    : (storeData?.selected || null);
  const [selected, setSelected] = useState(initialSelected);
  // For multiselection, only mark as submitted if we've actually submitted
  // Don't use store's hasSelected for multiselection - it might be stale
  const hasSelectedRef = useRef(
    allowMultiple
      ? false // Always start fresh for multiselection
      : (storeData?.hasSelected || false)
  );
  const submitTimeoutRef = useRef(null);

  // Reset when options change (new question)
  const optionsKey = JSON.stringify(options);
  const prevOptionsKeyRef = useRef(optionsKey);

  useEffect(() => {
    // Only reset if options actually changed (new question)
    if (optionsKey !== prevOptionsKeyRef.current) {
      setSelected(allowMultiple ? [...preselectedList] : null);
      hasSelectedRef.current = false;
      selectionStore.delete(storeKey);
      prevOptionsKeyRef.current = optionsKey;
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
        submitTimeoutRef.current = null;
      }
    }
  }, [optionsKey, storeKey, allowMultiple, preselectedKey]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
    };
  }, []);

  const handleSelect = (option) => {
    // Locked (already answered by a named technology) - cannot be toggled either way
    if (isLocked(option)) {
      return;
    }

    // For multiselection, only block if we've already submitted
    if (allowMultiple) {
      if (hasSelectedRef.current) {
        return;
      }
      if (disabled) {
        return;
      }
    } else {
      // Single selection: block if disabled
      if (disabled) {
        return;
      }
    }

    if (allowMultiple) {
      // Multiselection mode: toggle option in array
      const currentSelected = Array.isArray(selected) ? selected : [];
      const isCurrentlySelected = currentSelected.includes(option);

      const newSelected = isCurrentlySelected
        ? currentSelected.filter(opt => opt !== option)
        : [...currentSelected, option];

      setSelected(newSelected);

      // Store in global store for persistence
      selectionStore.set(storeKey, {
        selected: newSelected,
        hasSelected: false // Don't mark as submitted yet, allow more selections
      });

      // Clear existing timeout
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }

      // Only auto-submit if Continue button is NOT shown
      // If showContinueButton is true, user must click Continue button
      if (!showContinueButton && newSelected.length > 0) {
        const selectedForSubmission = [...newSelected];
        submitTimeoutRef.current = setTimeout(() => {
          if (hasSelectedRef.current || !onContinue) {
            return;
          }

          const valueToSend = selectedForSubmission.join(", ");

          // Mark as submitted FIRST to prevent double submission
          hasSelectedRef.current = true;

          // Update store to mark as submitted
          selectionStore.set(storeKey, {
            selected: selectedForSubmission,
            hasSelected: true
          });

          // Update state to disable buttons
          setSelected(selectedForSubmission);

          // Then submit
          onContinue(valueToSend);
        }, 4000);
      } else {
        // If all options deselected, cancel any pending submission
        if (submitTimeoutRef.current) {
          clearTimeout(submitTimeoutRef.current);
          submitTimeoutRef.current = null;
        }
      }
    } else {
      // Single selection mode: original behavior
      const storeData = selectionStore.get(storeKey);
      if (storeData?.hasSelected || hasSelectedRef.current) {
        return; // Already selected, block immediately
      }
      if (selected !== null) {
        return;
      }

      // Mark as selected immediately (synchronous) - BEFORE anything else
      hasSelectedRef.current = true;

      // Store in global store for persistence
      selectionStore.set(storeKey, {
        selected: option,
        hasSelected: true
      });

      // Update state (triggers re-render)
      setSelected(option);

      // Send selection immediately (no Continue button needed)
      if (onContinue) {
        onContinue(option);
      }
    }
  };

  // Check if options should be disabled - check store first, then ref, then state
  const currentStoreData = selectionStore.get(storeKey);
  const hasSelectedFromStore = currentStoreData?.hasSelected || false;

  const isDisabled = allowMultiple
    ? hasSelectedRef.current // In multiselection mode, disable ONLY if we've already submitted
    : disabled || hasSelectedFromStore || hasSelectedRef.current || selected !== null; // Single selection mode

  const handleContinue = () => {
    if (!onContinue || hasSelectedRef.current) {
      return;
    }

    const currentSelected = Array.isArray(selected) ? selected : [];
    if (currentSelected.length === 0) {
      return; // Don't submit if nothing is selected
    }

    // Mark as submitted FIRST to prevent double submission
    hasSelectedRef.current = true;

    // Update store to mark as submitted
    selectionStore.set(storeKey, {
      selected: currentSelected,
      hasSelected: true
    });

    // Format the value to send (comma-separated for backend)
    const valueToSend = currentSelected.join(", ");

    // Submit
    onContinue(valueToSend);
  };

  const currentSelected = Array.isArray(selected) ? selected : [];
  // A preselected deliverable already satisfies the question even before the user
  // taps anything else, so Continue must be available from the start.
  const hasSelections = currentSelected.length > 0 || preselectedList.length > 0;

  // Detect confirmation mode: slot is "confirmation.value" or options are ["Confirm", "Cancel"]
  const isConfirmationMode = slot === "confirmation.value" ||
    (options.length === 2 &&
     options.includes("Confirm") &&
     options.includes("Cancel"));

  return (
    <div className={`selection-options ${isConfirmationMode ? "confirmation-mode" : ""}`}>
      {allowMultiple && (
        <div className="selection-multi-hint">
          Select one or more options, then click Continue.
        </div>
      )}
      {allowMultiple && preselectedList.length > 0 && (
        <div className="selection-preselected-hint">
          Note: {preselectedList.join(", ")} {preselectedList.length > 1 ? "are" : "is"} already
          decided by what you described and can&apos;t be changed here.
        </div>
      )}
      {options.map((opt) => {
        const isSelectedOption = allowMultiple
          ? (Array.isArray(selected) && selected.includes(opt))
          : (selected === opt);
        const isLockedOption = isLocked(opt);
        const buttonDisabled = isDisabled || isLockedOption;

        // Determine button style for confirmation mode
        const isConfirmButton = opt === "Confirm";
        const isCancelButton = opt === "Cancel";
        const buttonClass = isConfirmationMode
          ? `selection-option-btn ${isConfirmButton ? "confirm-btn" : isCancelButton ? "cancel-btn" : ""} ${
              isSelectedOption ? "selected" : ""
            } ${isLockedOption ? "locked" : ""} ${isDisabled ? "disabled" : ""}`
          : `selection-option-btn ${
              isSelectedOption ? "selected" : ""
            } ${isLockedOption ? "locked" : ""} ${isDisabled ? "disabled" : ""}`;

        return (
          <button
            key={opt}
            className={buttonClass}
            onClick={() => handleSelect(opt)}
            disabled={buttonDisabled}
            title={isLockedOption ? "Already decided by what you described - can't be changed here." : undefined}
            style={{
              opacity: isDisabled ? 0.45 : 1,
              cursor: buttonDisabled ? "not-allowed" : "pointer",
              pointerEvents: buttonDisabled ? "none" : "auto"
            }}
          >
            {isLockedOption && (
              <span className="option-lock-tick" aria-hidden="true">✓ </span>
            )}
            {opt}
          </button>
        );
      })}

      {/* Show Continue button for multi-select with Continue button enabled */}
      {showContinueButton && allowMultiple && (
        <button
          className="selection-continue-btn"
          onClick={handleContinue}
          disabled={!hasSelections || hasSelectedRef.current}
          style={{
            opacity: (!hasSelections || hasSelectedRef.current) ? 0.5 : 1,
            cursor: (!hasSelections || hasSelectedRef.current) ? "not-allowed" : "pointer"
          }}
        >
          Continue
        </button>
      )}
    </div>
  );
}
